import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useMemo } from 'react';

import { useOrganization } from '@/hooks/useOrganization';
import { useUser } from '@/hooks/useUser';
import { useCommunicationChannelData } from '@/pages/communicationChannel/communicationChannel.queries';
import { useAllContactData } from '@/pages/contact/contact.queries';
import { restUrl } from '@/services/utils';

import {
    applyCustomPublicationTargetFilter,
    expandAndFilterChannels,
    filterOrganizations,
} from '../lib/publicationDestinations';
import type {
    ChannelDestination,
    ChannelTypeVerification,
    OrganizationDestination,
    PublicationMode,
} from '../types';
import { getPublicationAngularContext } from './publicationAngularBridge';

const useChannelTypeVerification = () => {
    return useQuery<ChannelTypeVerification>({
        queryKey: ['communicationchannelTypeVerification'],
        queryFn: async () => {
            const response = await axios.get<ChannelTypeVerification>(
                restUrl('communicationchannelTypeVerification'),
            );
            return response.data ?? {};
        },
    });
};

const usePublicationDestinations = (mode: PublicationMode) => {
    const { organization } = useOrganization();
    const { user } = useUser();
    const contactsQuery = useAllContactData(true);
    const channelsQuery = useCommunicationChannelData(true);
    const verificationQuery = useChannelTypeVerification();
    const angularContext = useMemo(() => getPublicationAngularContext(), []);

    const destinations = useMemo(() => {
        if (!contactsQuery.data || !channelsQuery.data || !verificationQuery.data) {
            return {
                organizations: [] as OrganizationDestination[],
                communicationChannels: [] as ChannelDestination[],
            };
        }

        const organizations = filterOrganizations(
            contactsQuery.data as OrganizationDestination[],
            organization?.id,
        );
        const communicationChannels = expandAndFilterChannels({
            channels: channelsQuery.data as ChannelDestination[],
            plans: angularContext.plans,
            verification: verificationQuery.data,
            mode,
            translateFormat: angularContext.translateFormat,
        });

        return applyCustomPublicationTargetFilter({
            organizations,
            communicationChannels,
            mode,
            customPublicationSettings: angularContext.customPublicationSettings,
            user: user ?? angularContext.user,
            organization: organization ?? angularContext.organization,
        });
    }, [
        angularContext,
        channelsQuery.data,
        contactsQuery.data,
        mode,
        organization,
        user,
        verificationQuery.data,
    ]);

    const isLoading = contactsQuery.isLoading
        || channelsQuery.isLoading
        || verificationQuery.isLoading;

    return {
        ...destinations,
        isLoading,
        plans: angularContext.plans,
        customPublicationSettings: angularContext.customPublicationSettings,
        user: user ?? angularContext.user,
        organization: organization ?? angularContext.organization,
    };
};

export { useChannelTypeVerification, usePublicationDestinations };
